//
//  SDKTextToken.ts
//  Supernova SDK
//
//  Created by Jiri Trecak.
//  Copyright © 2021 Supernova. All rights reserved.
//
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Imports
import { SupernovaError } from "../../core/errors/SDKSupernovaError";
import { UnreachableCaseError } from "../../utils/UnreachableCaseError";
import { TokenType } from "../enums/SDKTokenType";
import { Token } from "./SDKToken";
import { v4 as uuidv4 } from "uuid";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: -  Object Definition
export class StringCategoryToken extends Token {
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Constructor
    constructor(versionId, baseToken, value, alias, properties, propertyValues) {
        super(baseToken, versionId, properties, propertyValues);
        // The concrete subclass fixes T to the matching string value
        this.value = value;
        if (alias) {
            this.value.referencedTokenId = alias.id;
        }
    }
    static create(type, versionId, brandId, name, description, value, alias, properties, propertyValues) {
        var _a;
        const baseToken = {
            // @ts-expect-error TS(2322): Type 'undefined' is not assignable to type 'string... Remove this comment to see the full error message
            id: undefined, // Ommited id will create new token
            persistentId: uuidv4(),
            brandId,
            designSystemVersionId: versionId,
            type,
            meta: {
                name,
                description,
            },
            data: {},
            customPropertyOverrides: [],
        };
        if (value !== null && value !== undefined) {
            const tokenValue = {
                text: value.toString(),
                referencedTokenId: null,
            };
            return new this(versionId, baseToken, tokenValue, null, properties, propertyValues);
        }
        if (alias) {
            // Aliased value - copy and create raw from reference
            const tokenValue = {
                text: (_a = alias.value) === null || _a === void 0 ? void 0 : _a.text,
                referencedTokenId: alias.id,
            };
            return new this(versionId, baseToken, tokenValue, null, properties, propertyValues);
        }
        throw SupernovaError.fromMessage("String Token must be created using value or alias, but none was provided");
    }
    static stringValueFromDefinitionOrReference(definition, referenceResolver, tokenType) {
        if (referenceResolver.valueHasReference(definition) &&
            typeof definition === "string") {
            if (!referenceResolver.isBalancedReference(definition)) {
                // Internal syntax of reference corrupted
                throw new Error(`Invalid reference syntax in token value: ${definition}`);
            }
            if (referenceResolver.valueIsPureReference(definition)) {
                // When color is pure reference, we can immediately resolve it
                const reference = referenceResolver.lookupReferencedToken(definition);
                if (!reference) {
                    // NOTE: This might later be resolved (see Caviar default, fontWeight)
                    return undefined;
                }
                return {
                    referencedTokenId: reference.id,
                    text: reference.value.text,
                };
            }
            // When color is not a pure reference, we must resolve it further before we can resolve it
            const references = referenceResolver.lookupAllReferencedTokens(definition);
            if (!references) {
                // Still unable to solve the reference, continue looking in some other tokens
                return undefined;
            }
            // Resolved all internal references
            const resolvedValue = referenceResolver.replaceAllReferencedTokens(definition, references);
            return this.stringValueFromDefinition(resolvedValue, tokenType);
        }
        return this.stringValueFromDefinition(definition, tokenType);
    }
    static stringValueFromDefinition(definition, tokenType) {
        return {
            text: typeof definition === "string" ? definition : "",
            referencedTokenId: null,
        };
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Write
    toWriteObject() {
        const baseData = this.toBaseWriteObject();
        const specificData = baseData;
        specificData.data = StringCategoryToken.valueToWriteObject(this.value);
        return specificData;
    }
    static valueToWriteObject(value) {
        var _a;
        const valueObject = !value.referencedTokenId ? value.text : undefined;
        return {
            aliasTo: (_a = value.referencedTokenId) !== null && _a !== void 0 ? _a : undefined,
            // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
            value: valueObject,
        };
    }
}
export class StringToken extends StringCategoryToken {
    constructor() {
        super(...arguments);
        this.tokenType = TokenType.string;
    }
}
export class ProductCopyToken extends StringCategoryToken {
    constructor() {
        super(...arguments);
        this.tokenType = TokenType.productCopy;
    }
}
export class FontFamilyToken extends StringCategoryToken {
    constructor() {
        super(...arguments);
        this.tokenType = TokenType.fontFamily;
    }
}
export class FontWeightToken extends StringCategoryToken {
    constructor() {
        super(...arguments);
        this.tokenType = TokenType.fontWeight;
    }
}
export const resolveString = (type) => {
    switch (type) {
        case TokenType.string:
            return StringToken;
        case TokenType.productCopy:
            return ProductCopyToken;
        case TokenType.fontFamily:
            return FontFamilyToken;
        case TokenType.fontWeight:
            return FontWeightToken;
        default:
            throw new UnreachableCaseError(type);
    }
};
